/**
 * Single module for proficiency badge expiration + related PATCH (v2 badge API).
 *
 * - Named exports: UI helpers (no Pinia required).
 * - `useProficiencyBadgeExpirationStore()`: PATCH, optional **local overlay** from response
 *   (instant UI on every surface), `refreshGeneration` + `expirationSynced` for parent refetch.
 */
import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import dayjs from "dayjs";
import { updateBadge } from "@/api";

/** PATCH / serializer keys (single definition for Sonar duplicate-literal rules). */
const BADGE_PATCH_FIELDS = Object.freeze({
  expirationDate: "expiration_date",
  expiresInDaysFromObsolete: "expires_in_days_from_becoming_obsolete",
  status: "status",
});

/** Given / Obsolete editor: earliest selectable = tomorrow; else today (obsolete-days path). */
export function getExpirationPickerDateMinForEditorBadgeStatus(editorBadgeStatus) {
  return editorBadgeStatus === "Given" || editorBadgeStatus === "Obsolete" ? "tomorrow" : "today";
}

/**
 * Feature gate for proficiency expiration calendar UI (`ExpirationIcon` in `ReadinessBadge`).
 * Requires `idea-MDP-11` unless the parent passes `withoutFeatureFlag` (tests, Storybook, select tables).
 */
export function wantsProficiencyExpirationIcon({ withoutFeatureFlag, ideaMdp11 } = {}) {
  if (withoutFeatureFlag) return true;
  return !!ideaMdp11;
}

/**
 * Animated standing markers in `ReadinessBadge` (Given: black square; Obsolete+Current: pulsating red).
 * Gated by `idea-MDP-453` unless the parent passes `animatedStandingDotsWithoutFlag` (tests, Storybook).
 */
export function wantsAnimatedStandingDots({ animatedStandingDotsWithoutFlag, ideaMdp453 } = {}) {
  if (animatedStandingDotsWithoutFlag) return true;
  return !!ideaMdp453;
}

/**
 * Appointed diamond dot in `ReadinessBadge`. Gated by `idea-MDP-641` unless the parent
 * passes `appointedDotWithoutFlag` (tests, Storybook); with the flag off an Appointed badge falls back to Good Standing.
 */
export function wantsAppointedDot({ appointedDotWithoutFlag, ideaMdp641 } = {}) {
  if (appointedDotWithoutFlag) return true;
  return !!ideaMdp641;
}

function parseAccountId() {
  const raw = localStorage.getItem("currentAccountID");
  if (raw === null || raw === undefined || raw === "") return null;
  const direct = Number(raw);
  if (Number.isFinite(direct) && direct > 0) return direct;
  try {
    const n = Number(JSON.parse(raw));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function parseBadgeId(badgeId, legacyId) {
  const n = Number(badgeId ?? legacyId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function badgeOverlayKey(badgeId, legacyId) {
  const id = parseBadgeId(badgeId, legacyId);
  return id ? String(id) : "";
}

function apiStatusToDot(status) {
  const s = String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (s === "good_standing") return "Good Standing";
  if (s === "obsolete") return "Obsolete";
  if (s === "given") return "Given";
  return null;
}

/**
 * Fills overlay gaps from the PATCH request body (Sonar: keeps `buildOverlayFromPatchResponse` complexity low).
 * @param {Record<string, unknown>} overlay
 * @param {unknown} payload
 * @param {typeof BADGE_PATCH_FIELDS} fields
 */
function mergePatchPayloadIntoBadgeOverlay(overlay, payload, fields) {
  if (!payload || typeof payload !== "object") return;

  /** @type {Record<string, unknown>} */
  const p = payload;
  const { expirationDate, expiresInDaysFromObsolete, status } = fields;
  if (overlay[expirationDate] === undefined && expirationDate in p) {
    overlay[expirationDate] = p[expirationDate];
  }
  if (overlay[expiresInDaysFromObsolete] === undefined && expiresInDaysFromObsolete in p) {
    overlay[expiresInDaysFromObsolete] = p[expiresInDaysFromObsolete];
  }
  const dotFromPayload = apiStatusToDot(p[status]);
  if (!overlay.dot && dotFromPayload) overlay.dot = dotFromPayload;
}

/** Merge serializer PATCH response + request body into overlay fields ReadinessBadge understands. */
function buildOverlayFromPatchResponse(responseData, payload) {
  /** @type Record<string, unknown> */
  const o = {};
  const d = responseData && typeof responseData === "object" ? responseData : {};
  const fields = BADGE_PATCH_FIELDS;
  const { expirationDate, expiresInDaysFromObsolete, status } = fields;

  if (d[expirationDate] !== undefined) o[expirationDate] = d[expirationDate];
  if (d[expiresInDaysFromObsolete] !== undefined) {
    o[expiresInDaysFromObsolete] = d[expiresInDaysFromObsolete];
  }
  const dotFromApi = apiStatusToDot(d[status]);
  if (dotFromApi) o.dot = dotFromApi;

  mergePatchPayloadIntoBadgeOverlay(o, payload, fields);
  return o;
}

function normalizeExpirationForCompare(v) {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return dayjs(v).format("YYYY-MM-DD");
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

function normalizeDaysForCompare(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatDateForApi(value) {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return dayjs(d).format("YYYY-MM-DD");
}

function expirationPayloadFromEditor(editorData) {
  if (!editorData) return null;
  const { expirationDate, expiresInDaysFromObsolete } = BADGE_PATCH_FIELDS;
  if (editorData.expirationType === "on_this_date") {
    return {
      [expirationDate]: formatDateForApi(editorData.expirationDate),
      [expiresInDaysFromObsolete]: null,
    };
  }
  if (editorData.expirationType === "obsolete_days") {
    const days = Number.parseInt(String(editorData.days), 10);
    if (!Number.isFinite(days) || days <= 0) return null;
    return {
      [expirationDate]: null,
      [expiresInDaysFromObsolete]: days,
    };
  }
  return null;
}

export const useProficiencyBadgeExpirationStore = defineStore("proficiencyBadgeExpiration", () => {
  const pending = ref(false);
  const lastError = shallowRef(null);
  /** Incremented after every successful PATCH; watch in ReadinessBadge to emit `expirationSynced`. */
  const refreshGeneration = ref(0);
  /** Per-badge-id optimistic / PATCH-response fields until parent props catch up. */
  const patchOverlayByBadgeId = ref({});

  function resolveIds(badgeId, legacyId) {
    return {
      accountId: parseAccountId(),
      badgeNumericId: parseBadgeId(badgeId, legacyId),
    };
  }

  function setPatchOverlay(badgeNumericId, overlay) {
    const key = String(badgeNumericId);
    if (!key || key === "NaN" || !overlay || typeof overlay !== "object") return;
    patchOverlayByBadgeId.value = {
      ...patchOverlayByBadgeId.value,
      [key]: overlay,
    };
  }

  function getPatchOverlay(badgeId, legacyId) {
    const key = badgeOverlayKey(badgeId, legacyId);
    if (!key) return null;
    return patchOverlayByBadgeId.value[key] ?? null;
  }

  /**
   * When parent refetches and props match the overlay, drop overlay (avoid stale merges).
   */
  function reconcilePatchOverlay(badgeId, legacyId, propsLike) {
    const key = badgeOverlayKey(badgeId, legacyId);
    if (!key) return;
    const overlay = patchOverlayByBadgeId.value[key];
    if (!overlay || typeof overlay !== "object") return;

    const { expirationDate, expiresInDaysFromObsolete } = BADGE_PATCH_FIELDS;
    const propExp = normalizeExpirationForCompare(propsLike[expirationDate]);
    const ovrExp = normalizeExpirationForCompare(overlay[expirationDate]);
    const propDays = normalizeDaysForCompare(propsLike[expiresInDaysFromObsolete]);
    const ovrDays = normalizeDaysForCompare(overlay[expiresInDaysFromObsolete]);
    const dotOk = !overlay.dot || overlay.dot === propsLike.dot;
    const expOk = propExp === ovrExp;
    const daysOk = ovrDays === propDays;

    if (dotOk && expOk && daysOk) {
      const next = { ...patchOverlayByBadgeId.value };
      delete next[key];
      patchOverlayByBadgeId.value = next;
    }
  }

  async function patchBadge(badgeId, legacyId, payload) {
    lastError.value = null;
    const { accountId, badgeNumericId } = resolveIds(badgeId, legacyId);
    if (!accountId || !badgeNumericId || !payload || typeof payload !== "object") {
      return false;
    }
    pending.value = true;
    try {
      const res = await updateBadge(accountId, badgeNumericId, payload);
      refreshGeneration.value += 1;
      const overlay = buildOverlayFromPatchResponse(res?.data, payload);
      if (Object.keys(overlay).length) {
        setPatchOverlay(badgeNumericId, overlay);
      }
      return true;
    } catch (e) {
      lastError.value = e;
      return false;
    } finally {
      pending.value = false;
    }
  }

  async function clearExpiration(badgeId, legacyId) {
    const { expirationDate, expiresInDaysFromObsolete } = BADGE_PATCH_FIELDS;
    return patchBadge(badgeId, legacyId, {
      [expirationDate]: null,
      [expiresInDaysFromObsolete]: null,
    });
  }

  async function saveExpirationFromEditor(badgeId, legacyId, editorData) {
    const body = expirationPayloadFromEditor(editorData);
    if (!body) return false;
    return patchBadge(badgeId, legacyId, body);
  }

  async function markObsolete(badgeId, legacyId) {
    return patchBadge(badgeId, legacyId, { [BADGE_PATCH_FIELDS.status]: "obsolete" });
  }

  async function restoreGoodStanding(badgeId, legacyId) {
    return patchBadge(badgeId, legacyId, { [BADGE_PATCH_FIELDS.status]: "good_standing" });
  }

  return {
    pending,
    lastError,
    refreshGeneration,
    patchOverlayByBadgeId,
    getPatchOverlay,
    reconcilePatchOverlay,
    patchBadge,
    clearExpiration,
    saveExpirationFromEditor,
    markObsolete,
    restoreGoodStanding,
  };
});
